// Local persistence (GDD §42.1): decks, settings, and match history in localStorage.
// Loads validate every card id against the registry and silently drop unknowns, so
// content renames never brick a save. Unknown versions reset to defaults.

import { FACTIONS } from '../content/factions';
import { orderDeckByAscension } from '../content/deckOrder';
import { defs } from '../content/registry';
import { MAPS } from '../content/map';
import type { FactionId } from '../content/types';

const DECKS_KEY = 'pa:decks:v1';
const SETTINGS_KEY = 'pa:settings:v1';
const PROFILE_KEY = 'pa:profile:v1';

export const DECK_SIZE = 10; // owner feedback: one visible row of 10 cards (was 14)

// ---------------------------------------------------------------- decks

export interface SavedDeck {
  id: string;
  name: string;
  faction: FactionId;
  /** ≤DECK_SIZE card ids; canonical Ascension order = in-match hotkey order */
  cards: string[];
}

interface DeckStore {
  version: 1;
  decks: SavedDeck[];
  lastUsedByFaction: Partial<Record<FactionId, string>>;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full/blocked: persistence is best-effort
  }
}

/** Drop unknown/foreign/duplicate card ids and clamp to deck size. */
export function sanitizeDeckCards(faction: FactionId, cards: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const cid of cards) {
    const def = defs.cards.get(cid);
    if (!def || def.faction !== faction || def.deckEligible === false) continue;
    if (seen.has(cid)) continue;
    seen.add(cid);
    out.push(cid);
    if (out.length >= DECK_SIZE) break;
  }
  return orderDeckByAscension(out);
}

function loadDeckStore(): DeckStore {
  const raw = readJson<DeckStore>(DECKS_KEY);
  if (!raw || raw.version !== 1 || !Array.isArray(raw.decks)) {
    return { version: 1, decks: [], lastUsedByFaction: {} };
  }
  const decks: SavedDeck[] = [];
  for (const d of raw.decks) {
    if (!d || typeof d.id !== 'string' || typeof d.name !== 'string') continue;
    if (!(d.faction in FACTIONS)) continue;
    decks.push({ ...d, cards: sanitizeDeckCards(d.faction, d.cards ?? []) });
  }
  return { version: 1, decks, lastUsedByFaction: raw.lastUsedByFaction ?? {} };
}

export function loadDecks(faction?: FactionId): SavedDeck[] {
  const store = loadDeckStore();
  return faction ? store.decks.filter((d) => d.faction === faction) : store.decks;
}

export function saveDeck(deck: SavedDeck): void {
  const store = loadDeckStore();
  const idx = store.decks.findIndex((d) => d.id === deck.id);
  const clean = { ...deck, cards: sanitizeDeckCards(deck.faction, deck.cards) };
  if (idx >= 0) store.decks[idx] = clean;
  else store.decks.push(clean);
  writeJson(DECKS_KEY, store);
}

export function deleteDeck(deckId: string): void {
  const store = loadDeckStore();
  store.decks = store.decks.filter((d) => d.id !== deckId);
  for (const f of Object.keys(store.lastUsedByFaction) as FactionId[]) {
    if (store.lastUsedByFaction[f] === deckId) delete store.lastUsedByFaction[f];
  }
  writeJson(DECKS_KEY, store);
}

export function lastUsedDeckId(faction: FactionId): string | null {
  return loadDeckStore().lastUsedByFaction[faction] ?? null;
}

export function markDeckUsed(faction: FactionId, deckId: string): void {
  const store = loadDeckStore();
  store.lastUsedByFaction[faction] = deckId;
  writeJson(DECKS_KEY, store);
}

export function newDeckId(): string {
  // no Date.now dependence needed for uniqueness within a store
  const existing = new Set(loadDeckStore().decks.map((d) => d.id));
  let n = 1;
  while (existing.has(`deck-${n}`)) n++;
  return `deck-${n}`;
}

// ---------------------------------------------------------------- settings

export type DisplayMode = 'fullscreen' | 'borderless' | 'windowed';
export type QualityPreset = 'low' | 'medium' | 'high' | 'ultra';

/** Option lists are the single source of truth shared by the Settings UI and the loader's
 *  coercion — add/rename a value here and both the dropdown and validation follow. */
export const DISPLAY_MODES = ['fullscreen', 'borderless', 'windowed'] as const;
export const QUALITY_PRESETS = ['low', 'medium', 'high', 'ultra'] as const;
export const RESOLUTIONS = [
  { value: '1280x720', label: '1280 × 720 (16:9)' },
  { value: '1600x900', label: '1600 × 900 (16:9)' },
  { value: '1920x1080', label: '1920 × 1080 (16:9)' },
  { value: '2560x1440', label: '2560 × 1440 (16:9)' },
  { value: '3840x2160', label: '3840 × 2160 (16:9)' },
] as const;

export interface SettingsData {
  version: 4;
  // ---- general (all live-applied) ----
  /** camera pan speed multiplier */
  panSpeed: number;
  zoomInvert: boolean;
  /** HUD scale (CSS zoom on the HUD layer) */
  hudScale: number;
  showFps: boolean;
  // ---- graphics ----
  /** display mode uses the Fullscreen API; quality and render scale apply to WebGL live */
  displayMode: DisplayMode;
  resolution: string;
  quality: QualityPreset;
  vsync: boolean;
  /** internal WebGL resolution multiplier; CSS layout remains native resolution */
  renderScale: number;
  /** ACES tone-map exposure; lower values preserve highlights and deepen material shading */
  sceneExposure: number;
  reduceMotion: boolean;
  foilEffects: boolean;
  largeCardText: boolean;
  // ---- audio (0–100; live music and event cues) ----
  masterVol: number;
  musicVol: number;
  sfxVol: number;
  muteWhenMinimized: boolean;
  // ---- gameplay ----
  /** live: pan the camera when the cursor rests near a screen edge */
  edgeScroll: boolean;
  /** number-key card hotkeys cast at the live battlefield cursor; card-button clicks still arm placement */
  quickCast: boolean;
  healthBars: boolean;
  cameraShake: boolean;
  /** 0 = instant camera (pre-0.10 behaviour), 1 = default easing on pan and zoom */
  cameraSmoothing: number;
  // ---- advanced / dev (kept from the UAT shell) ----
  /** show the UAT cheat panel in-match */
  cheats: boolean;
  /** 'all' shows every alert; 'important' mutes minor economy chatter */
  alertVerbosity: 'all' | 'important';
}

export const DEFAULT_SETTINGS: SettingsData = {
  version: 4,
  panSpeed: 1,
  zoomInvert: false,
  hudScale: 1,
  showFps: true,
  displayMode: 'fullscreen',
  resolution: '1920x1080',
  quality: 'high',
  vsync: true,
  renderScale: 1,
  sceneExposure: 0.88,
  reduceMotion: false,
  foilEffects: true,
  largeCardText: false,
  masterVol: 80,
  musicVol: 45,
  sfxVol: 92,
  muteWhenMinimized: true,
  edgeScroll: true,
  quickCast: true,
  healthBars: true,
  cameraShake: true,
  cameraSmoothing: 1,
  cheats: true, // UAT build default; flip off for release
  alertVerbosity: 'all',
};

export function loadSettings(): SettingsData {
  const raw = readJson<Partial<Omit<SettingsData, 'version'>> & { version?: number }>(SETTINGS_KEY);
  // accept older saves and backfill newly functional graphics/accessibility fields
  if (!raw || (raw.version !== 1 && raw.version !== 2 && raw.version !== 3 && raw.version !== 4)) return { ...DEFAULT_SETTINGS };
  const m = { ...DEFAULT_SETTINGS, ...raw, version: 4 as const };
  return {
    ...m,
    panSpeed: clamp(m.panSpeed, 0.4, 2.5),
    hudScale: clamp(m.hudScale, 0.7, 1.4),
    renderScale: clamp(m.renderScale, 0.5, 1),
    sceneExposure: clamp(m.sceneExposure, 0.65, 1.15),
    cameraSmoothing: clamp(m.cameraSmoothing, 0, 1),
    masterVol: clampInt(m.masterVol, 0, 100),
    musicVol: clampInt(m.musicVol, 0, 100),
    sfxVol: clampInt(m.sfxVol, 0, 100),
    displayMode: DISPLAY_MODES.includes(m.displayMode) ? m.displayMode : 'fullscreen',
    quality: QUALITY_PRESETS.includes(m.quality) ? m.quality : 'high',
    resolution: RESOLUTIONS.some((r) => r.value === m.resolution) ? m.resolution : '1920x1080',
    alertVerbosity: m.alertVerbosity === 'important' ? 'important' : 'all',
  };
}

export function saveSettings(s: SettingsData): void {
  writeJson(SETTINGS_KEY, s);
}

function clamp(v: unknown, lo: number, hi: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 1;
  return Math.max(lo, Math.min(hi, n));
}

function clampInt(v: unknown, lo: number, hi: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : lo;
  return Math.max(lo, Math.min(hi, n));
}

// ---------------------------------------------------------------- profile / match history

export interface MatchResult {
  at: number; // epoch ms
  mode: import('../content/types').MatchMode;
  mapId: import('../content/types').MapId;
  playerFaction: FactionId;
  allyFactions: FactionId[];
  enemyFactions: FactionId[];
  result: 'victory' | 'defeat' | 'abandoned';
  source: 'single' | 'online';
  durationSec: number;
}

interface LegacyMatchResult {
  at: number;
  playerFaction: FactionId;
  aiFaction: FactionId;
  won: boolean;
  durationSec: number;
}

interface ProfileStoreV1 {
  version: 1;
  results: LegacyMatchResult[];
}

interface ProfileStoreV2 {
  version: 2;
  results: MatchResult[];
}

export function loadProfile(): MatchResult[] {
  const raw = readJson<ProfileStoreV1 | ProfileStoreV2>(PROFILE_KEY);
  if (!raw || !Array.isArray(raw.results)) return [];
  if (raw.version === 1) {
    return raw.results
      .filter((r) => r && r.playerFaction in FACTIONS && r.aiFaction in FACTIONS)
      .map((r) => ({
        at: r.at,
        mode: '1v1',
        mapId: 'crownline-crossing',
        playerFaction: r.playerFaction,
        allyFactions: [],
        enemyFactions: [r.aiFaction],
        result: r.won ? 'victory' : 'defeat',
        source: 'single',
        durationSec: r.durationSec,
      }));
  }
  if (raw.version !== 2) return [];
  const results = raw.results.filter((r) =>
    r && r.playerFaction in FACTIONS && Array.isArray(r.allyFactions) && Array.isArray(r.enemyFactions) &&
    r.mapId in MAPS && MAPS[r.mapId].mode === r.mode &&
    ['victory', 'defeat', 'abandoned'].includes(r.result) && ['single', 'online'].includes(r.source) &&
    r.allyFactions.every((f) => f in FACTIONS) && r.enemyFactions.every((f) => f in FACTIONS),
  );
  return results.map((result) => {
    const { seed: _legacySeed, ...withoutSeed } = result as MatchResult & { seed?: number };
    return withoutSeed;
  });
}

export function recordResult(result: MatchResult): void {
  const results = loadProfile();
  results.push(result);
  // keep the file bounded; the profile table aggregates, the list shows the tail
  writeJson(PROFILE_KEY, { version: 2, results: results.slice(-200) } satisfies ProfileStoreV2);
}

/** W/L per player-faction × AI-faction matchup for offline 1v1. */
export function matchupTable(): Map<string, { wins: number; losses: number }> {
  const table = new Map<string, { wins: number; losses: number }>();
  for (const r of loadProfile()) {
    if (r.mode !== '1v1' || r.source !== 'single' || !r.enemyFactions[0]) continue;
    const key = `${r.playerFaction}|${r.enemyFactions[0]}`;
    const cell = table.get(key) ?? { wins: 0, losses: 0 };
    if (r.result === 'victory') cell.wins++;
    else if (r.result === 'defeat') cell.losses++;
    table.set(key, cell);
  }
  return table;
}
